import { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, Sparkles, Loader2, CheckCircle, X } from 'lucide-react'
import MarkdownContent from './chat/MarkdownContent'
import { announcementApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'

let nextMsgId = 0
const nextId = () => `msg-${++nextMsgId}`

export default function AIChatPanel({ buildingId, onApply, onClose }) {
  const [messages, setMessages] = useState([
    {
      id: nextId(),
      text: "Hi! Describe the announcement you'd like to create. I can help with content, scheduling, priority, and recipient targeting.\n\nFor example: *\"Fire inspection on the 15th of next month, send a week before to all residents\"*",
      isFromUser: false,
      timestamp: new Date(),
    },
  ])
  const [currentAnnouncementData, setCurrentAnnouncementData] = useState(null)
  const [inputText, setInputText] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const [isFinal, setIsFinal] = useState(false)
  const [error, setError] = useState(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToEnd = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const sendMessage = async (text) => {
    if (!text.trim() || isThinking) return

    const userMsg = {
      id: nextId(),
      text: text.trim(),
      isFromUser: true,
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, userMsg])
    setInputText('')
    setIsThinking(true)
    setError(null)
    setIsFinal(false)
    scrollToEnd()

    try {
      const response = await announcementApi.generateDraft({
        command: text.trim(),
        buildingId,
        announcementData: currentAnnouncementData,
      })

      if (response.data) {
        if (response.data.announcementData) {
          setCurrentAnnouncementData(response.data.announcementData)
        }

        const aiMsg = {
          id: nextId(),
          text: response.data.textResponse || 'Draft updated.',
          isFromUser: false,
          suggestions: response.data.suggestedReplies || [],
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, aiMsg])

        if (response.data.isFinal && response.data.announcementData) {
          setIsFinal(true)
        }
      }
    } catch (err) {
      setError(extractErrorMessage(err))
      const errMsg = {
        id: nextId(),
        text: 'Sorry, something went wrong. Please try again.',
        isFromUser: false,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errMsg])
    } finally {
      setIsThinking(false)
      scrollToEnd()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(inputText)
    }
  }

  const handleApply = () => {
    if (currentAnnouncementData && onApply) {
      onApply(currentAnnouncementData)
    }
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold text-sm">AI Announcement Assistant</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ maxHeight: '400px' }}>
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.isFromUser ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${msg.isFromUser ? 'order-1' : 'order-2'}`}>
              <div className="flex items-start gap-2">
                {!msg.isFromUser && (
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-primary-600" />
                  </div>
                )}
                <div
                  className={`rounded-2xl px-4 py-2.5 ${
                    msg.isFromUser
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.isFromUser ? (
                    <p className="text-sm">{msg.text}</p>
                  ) : (
                    <div className="text-sm">
                      <MarkdownContent>{msg.text}</MarkdownContent>
                    </div>
                  )}
                </div>
                {msg.isFromUser && (
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>

              {/* Suggested replies */}
              {!msg.isFromUser && msg.suggestions && msg.suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 ml-9">
                  {msg.suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(suggestion)}
                      disabled={isThinking}
                      className="text-xs px-3 py-1.5 rounded-full border border-primary-300 text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Thinking indicator */}
        {isThinking && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-primary-600" />
              </div>
              <div className="bg-gray-100 rounded-2xl px-4 py-2.5 flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
                <span className="text-sm text-gray-500">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Apply to Form button */}
      {isFinal && currentAnnouncementData && (
        <div className="px-4 py-3 border-t border-gray-200 bg-green-50">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Announcement ready!</span>
          </div>
          <button
            onClick={handleApply}
            className="w-full px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Apply to Form
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-xs">
          {error}
        </div>
      )}

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isFinal ? 'Refine the announcement or apply to form...' : 'Describe your announcement...'}
            disabled={isThinking}
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
          />
          <button
            onClick={() => sendMessage(inputText)}
            disabled={!inputText.trim() || isThinking}
            className="p-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
